import { useCallback, useEffect, useState } from "react";
import { IDepartment } from "../../models/department";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AppState } from "../../store/configureStore";
import { getDepartments } from "../../services/departmentsService";
import { DepartmentRow } from "./DepartmentRow";
import { Icon, Loader, Pagination, Segment, Table, Button } from "semantic-ui-react";
import { AddDepartmentForm } from "./AddDepartmentForm";
import { UserRole } from "../../actions/authentificationActions";
import "./Departments.css";

export const Departments = () => {
    const user = useSelector((state: AppState) => state.user);
    const [loading, setLoading] = useState(true);
    const loc = useLocation();
    const [page, setPage] = useState(loc.state ? (loc.state as number) : 1);
    const [totalRows, setTotalRows] = useState(0);
    const [departments, setDepartments] = useState<IDepartment[]>([]);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchDepartments = useCallback(async () => {
        setLoading(true);
        const res = await getDepartments(page);
        if (res.rows) {
            setDepartments(res.rows as IDepartment[]);
            setTotalRows(res.totalRows);
        }
        setLoading(false);
    }, [page]);

    useEffect(() => {
        fetchDepartments();
    }, [fetchDepartments]);

    const handleRemoveDepartment = (d: IDepartment) =>
        setDepartments([...departments].filter(x => x !== d));

    const handleFetchDepartments = () => {
        setPage(1);
        fetchDepartments();
    };

    return (
        <div className="departments-container">
            <div className="container-header">
                <h1>Departments</h1>
                {user.role === UserRole.Admin && (
                    <Button
                        secondary
                        content="Add new department"
                        icon="plus"
                        onClick={() => setModalOpen(true)}
                    />
                )}
            </div>
            <div className="departments-display">
                <div className="departments-table">
                    {loading ? (
                        <Loader active inline="centered" size="huge" />
                    ) : (
                        <Segment.Group>
                            <Segment />
                            <Segment className="table-container">
                                <Table basic="very">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell>Name</Table.HeaderCell>
                                            <Table.HeaderCell>
                                                No. of department members
                                            </Table.HeaderCell>
                                            <Table.HeaderCell>Actions</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {departments.length === 0 ? (
                                            <Table.Row>
                                                <Table.Cell textAlign="center" colSpan="5">
                                                    No matching records found
                                                </Table.Cell>
                                            </Table.Row>
                                        ) : (
                                            departments.map(x => (
                                                <DepartmentRow
                                                    key={x.guid}
                                                    department={x}
                                                    handleRemoveDepartment={handleRemoveDepartment}
                                                    page={page + ""}
                                                />
                                            ))
                                        )}
                                        <Table.Row />
                                    </Table.Body>
                                </Table>
                            </Segment>
                            <Segment>
                                {totalRows > 10 && (
                                    <Pagination
                                        size="small"
                                        activePage={page}
                                        onPageChange={(e, data: any) => setPage(+data.activePage)}
                                        ellipsisItem={{
                                            content: <Icon name="ellipsis horizontal" />,
                                        }}
                                        firstItem={{
                                            content: <Icon name="angle double left" />,
                                        }}
                                        lastItem={{
                                            content: <Icon name="angle double right" />,
                                        }}
                                        prevItem={{ content: <Icon name="angle left" /> }}
                                        nextItem={{ content: <Icon name="angle right" /> }}
                                        totalPages={Math.ceil(totalRows / 10)}
                                    />
                                )}
                            </Segment>
                            <AddDepartmentForm
                                open={modalOpen}
                                closeModal={() => setModalOpen(false)}
                                handleAddDepartment={handleFetchDepartments}
                            />
                        </Segment.Group>
                    )}
                </div>
            </div>
        </div>
    );
};
